/**
 * De afdrukken bij een uitvaart (mockup, tabblad Afdrukken): certificaat, herinneringskaart,
 * begeleidende brief, as-retourbrief, de twee labels, het adresetiket en de factuur.
 *
 * <p>Client-side, net als de voerstickers en het adresetiket: wat je in het voorbeeld ziet is wat
 * er uit de printer komt. De teksten zijn Nederlands; de server onthoudt alleen dát er is
 * afgedrukt. Elke afdruk is een functie van dezelfde dossiergegevens naar één html-pagina.
 */

export interface AfdrukDossier {
  dossierNummer: string | null;
  dierNaam: string | null;
  diersoort: string | null;
  ras: string | null;
  gewichtKg: number | null;
  /** Staat op de crematiefactuur onder "Chipnummer". */
  chipnummer?: string | null;
  datumOverlijden: string | null;
  verwerkingsvorm: string | null;
  uitvaartvormNaam: string | null;
  planningDatum: string | null;
  identificatieNummer: string | null;
  asUrnNummer: string | null;
  retourwijze: string | null;
  grafnummer: string | null;
  eigenaarNaam: string | null;
  eigenaarAdres: string | null;
  eigenaarPostcode: string | null;
  eigenaarPlaats: string | null;
  organisatie: string;
  regels: { omschrijving: string | null; aantal: number | null; prijs: number | null; btwPercentage: number | null; regelTotaal: number | null }[];
  subtotaal: number | null;
  btwTotaal: number | null;
  totaalBedrag: number | null;
  betaaldBedrag: number | null;
  openstaandBedrag: number | null;
  factuur: { factuurNummer: string | null; factuurDatum: string | null; vervaldatum: string | null } | null;
}

export type AfdrukSoort =
  | "FACTUUR"
  | "CREMATIEFACTUUR"
  | "CREMATIECERTIFICAAT"
  | "HERINNERINGSKAART"
  | "BEGELEIDENDE_BRIEF"
  | "AS_RETOURBRIEF"
  | "IDENTIFICATIELABEL"
  | "URNLABEL"
  | "ADRESETIKET";

export const AFDRUKKEN: { soort: AfdrukSoort; formaat: string; alleenMetFactuur?: boolean }[] = [
  { soort: "FACTUUR", formaat: "A4", alleenMetFactuur: true },
  { soort: "CREMATIEFACTUUR", formaat: "A4 · voorgedrukt papier", alleenMetFactuur: true },
  { soort: "CREMATIECERTIFICAAT", formaat: "A4" },
  { soort: "HERINNERINGSKAART", formaat: "A5" },
  { soort: "BEGELEIDENDE_BRIEF", formaat: "A4" },
  { soort: "AS_RETOURBRIEF", formaat: "A4" },
  { soort: "IDENTIFICATIELABEL", formaat: "99010 · 76×25 mm" },
  { soort: "URNLABEL", formaat: "99010 · 76×25 mm" },
  { soort: "ADRESETIKET", formaat: "99010 · 76×25 mm" },
];

/**
 * "Geachte familie van Diessen," uit "Fam. M van Diessen".
 *
 * <p>Het bronrapport heeft de aanhef, het tussenvoegsel en de achternaam als losse velden. Hier is
 * alleen de volledige naam beschikbaar, dus wordt het voorvoegsel eruit gelezen. Herkent hij het
 * niet, dan blijft de naam staan zoals hij is — een verkeerde aanhef op een condoleance is erger
 * dan een zakelijke.
 */
export function aanhefUit(naam: string | null | undefined): string {
  const heel = (naam ?? "").trim();
  if (!heel) return "Geachte heer, mevrouw,";
  const vormen: [RegExp, string][] = [
    [/^fam(\.|ilie)?\s+/i, "familie"],
    [/^dhr\.?\s+/i, "heer"],
    [/^mevr?\.?\s+/i, "mevrouw"],
  ];
  for (const [patroon, woord] of vormen) {
    if (patroon.test(heel)) {
      // Wat er na het voorvoegsel staat is "M van Diessen"; de losse voorletter hoort niet in een
      // aanhef, dus die valt weg zodra er meer achter staat.
      const rest = heel.replace(patroon, "").trim();
      const delen = rest.split(/\s+/);
      const zonderVoorletters = delen.length > 1 && /^[A-Z]\.?$/.test(delen[0])
        ? delen.slice(1).join(" ") : rest;
      return `Geachte ${woord} ${zonderVoorletters},`;
    }
  }
  return `Geachte ${heel},`;
}

/**
 * Wat er met het dier is gebeurd, in de taal van het certificaat: gecremeerd, geresomeerd of
 * begraven. Het bronrapport leidt dit af uit losse vinkjes per vorm; hier staat de vorm zelf in het
 * dossier.
 */
export function verwerkingWoord(vorm: string | null | undefined): string {
  const v = (vorm ?? "").toUpperCase();
  if (v.includes("RESOMEREN")) return "geresomeerd";
  if (v.includes("BEGRAF") || v.includes("BEGRAVEN")) return "begraven";
  return "gecremeerd";
}

/**
 * " om 14:30 uur", of niets.
 *
 * <p>De uitvaartdatum draagt de tijd in zich; een los tijdveld bestaat niet. Middernacht telt hier
 * als "geen tijd opgegeven" — dat is wat het scherm toont wanneer er niets is ingevuld, en
 * "gecremeerd om 00:00 uur" op een condoleance is erger dan geen tijd.
 */
export function tijdstipUit(iso: string | null | undefined): string {
  if (!iso || iso.length <= 10) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  if (d.getHours() === 0 && d.getMinutes() === 0) return "";
  const hh = String(d.getHours()).padStart(2, "0");
  const mm = String(d.getMinutes()).padStart(2, "0");
  return ` om ${hh}:${mm} uur`;
}

/** Het gedicht op het certificaat. Letterlijk uit het bronrapport overgenomen. */
const CERTIFICAAT_GEDICHT = [
  "Nog nooit was er zoveel verdriet",
  "Dan op het moment dat jij deze wereld verliet",
  "Nog nooit voelde iets zo leeg",
  "Dan op het moment dat jij je vleugels kreeg",
  "Maar daar waar jouw leven",
  "Eindigde, eindigt niet jouw verhaal",
  "Want jij leeft voort in liefde",
  "In de liefde van ons allemaal.",
];

/**
 * De btw uitgesplitst per tarief, uit de regels zelf.
 *
 * <p>Het bronrapport heeft hier twee kant-en-klare velden (totaal_btw_laag en totaal_btw_hoog) met
 * 9 en 21 hard erin. Hier wordt het gerekend uit wat er op de regels staat, zodat een derde tarief
 * of een tariefwijziging vanzelf meekomt in plaats van stil te verdwijnen.
 */
export function btwPerTarief(
  regels: { prijs: number | null; aantal: number | null; btwPercentage: number | null; regelTotaal: number | null }[],
): { percentage: number; bedrag: number }[] {
  const per = new Map<number, number>();
  for (const rg of regels) {
    const pct = rg.btwPercentage ?? 0;
    if (pct <= 0) continue;
    const excl = rg.regelTotaal ?? (rg.prijs ?? 0) * (rg.aantal ?? 1);
    per.set(pct, (per.get(pct) ?? 0) + (excl * pct) / 100);
  }
  return [...per.entries()]
    .sort((a, b) => a[0] - b[0])
    .map(([percentage, bedrag]) => ({ percentage, bedrag: Math.round(bedrag * 100) / 100 }));
}

function esc(s: string | null | undefined): string {
  return (s ?? "").replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
}

function datum(iso: string | null | undefined): string {
  if (!iso) return "—";
  const d = new Date(iso.length === 10 ? iso + "T00:00:00" : iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString("nl-NL", { day: "2-digit", month: "2-digit", year: "numeric" });
}

function datumLang(iso: string | null | undefined): string {
  if (!iso) return "—";
  const d = new Date(iso.length === 10 ? iso + "T00:00:00" : iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString("nl-NL", { weekday: "long", day: "numeric", month: "long", year: "numeric" });
}

function euro(n: number | null | undefined): string {
  return n == null ? "—" : new Intl.NumberFormat("nl-NL", { style: "currency", currency: "EUR" }).format(n);
}

const STIJL = `
  body { font-family: system-ui, "Segoe UI", Arial, sans-serif; color: #25313b; margin: 0; }
  .blad { padding: 18mm 20mm; }
  .kop { display: flex; justify-content: space-between; align-items: baseline; border-bottom: 2px solid #25313b; padding-bottom: 4mm; margin-bottom: 8mm; }
  .kop strong { font-size: 15pt; }
  .kop span { color: #5a6b78; font-size: 10.5pt; }
  h1 { font-size: 22pt; margin: 0 0 6mm; letter-spacing: -0.02em; }
  h2 { font-size: 13pt; margin: 8mm 0 3mm; }
  p { font-size: 11.5pt; line-height: 1.55; margin: 0 0 3mm; }
  .stil { color: #5a6b78; }
  table { border-collapse: collapse; width: 100%; font-size: 11pt; }
  th, td { text-align: left; padding: 2mm 2mm; border-bottom: 1px solid #c8d2d9; vertical-align: top; }
  th { color: #5a6b78; font-weight: 600; font-size: 10pt; }
  td.r, th.r { text-align: right; white-space: nowrap; }
  .totaal td { border: 0; font-weight: 700; }
  .feiten { display: grid; grid-template-columns: 38mm 1fr; gap: 1.5mm 4mm; font-size: 11.5pt; margin: 4mm 0 8mm; }
  .feiten dt { color: #5a6b78; } .feiten dd { margin: 0; font-weight: 600; }
  /* De crematiefactuur ligt op voorgedrukt papier, dus staat alles op een vaste plek in plaats van
     mee te schuiven met de inhoud. De maten komen uit crematorium-factuur-report.jrxml, omgerekend
     van punten naar millimeters (1 pt = 0,3528 mm) op een A4 met marge nul. Wie de indeling
     verandert, verschuift hem ten opzichte van wat er al op het papier gedrukt staat. */
  .vf { position: relative; width: 210mm; height: 297mm; font-size: 10pt; }
  .vf .p { position: absolute; margin: 0; line-height: 1.35; }
  .vf .r { text-align: right; }
  .vf .vet { font-weight: 700; }
  .vf .regels { position: absolute; left: 24.7mm; right: 24.7mm; top: 173.6mm; height: 45mm; overflow: hidden; }
  .vf .regels table { width: 100%; border-collapse: collapse; font-size: 10pt; }
  .vf .regels td { border: 0; padding: 0 0 1.6mm; vertical-align: top; }
  .vf .regels td.b { text-align: right; white-space: nowrap; }
  .vf .lijn { position: absolute; border-top: 0.4mm solid #25313b; }
  .cert { text-align: center; padding: 12mm 10mm; border: 3px double #25313b; border-radius: 4mm; }
  .cert h1 { font-size: 26pt; } .cert .naam { font-size: 32pt; font-weight: 800; margin: 6mm 0; }
  /* De aanhef staat links en de rest gecentreerd: het is een brief die overgaat in een oorkonde. */
  .cert-aanhef { text-align: left; margin: 0 0 8mm; }
  .cert-zin { font-size: 13pt; line-height: 1.6; margin: 0 0 10mm; }
  .cert-gedicht { font-style: italic; line-height: 1.8; margin: 0 0 6mm; }
  .cert-oneindig { font-size: 18pt; font-weight: 700; letter-spacing: 0.08em; margin: 0 0 10mm; }
  .cert-namens { line-height: 1.6; margin: 0 0 6mm; }
  .kaart { width: 148mm; min-height: 105mm; padding: 14mm; box-sizing: border-box; border: 1px solid #c8d2d9; border-radius: 3mm; text-align: center; }
  .kaart .naam { font-size: 24pt; font-weight: 800; margin: 4mm 0; }
  .label { width: 76mm; height: 25mm; box-sizing: border-box; padding: 2.5mm 3mm; border: 1px dashed #c8d2d9; border-radius: 2mm; font-size: 9.5pt; line-height: 1.3; }
  .label strong { font-size: 12pt; display: block; }
  .etiket { display: inline-block; min-width: 76mm; border: 1px dashed #c8d2d9; border-radius: 3mm; padding: 6mm 8mm; }
  .etiket .naam { font-size: 14pt; font-weight: 800; margin-bottom: 2mm; }
  .etiket .regel { font-size: 12pt; line-height: 1.45; }
  .onder { margin-top: 12mm; font-size: 10pt; color: #5a6b78; }
`;

function pagina(titel: string, formaat: "A4" | "A5" | "label", inhoud: string): string {
  const size = formaat === "A4" ? "A4" : formaat === "A5" ? "A5 landscape" : "76mm 25mm";
  return `<!doctype html>
<html lang="nl"><head><meta charset="utf-8" /><title>${esc(titel)}</title>
<style>@page { size: ${size}; margin: ${formaat === "label" ? "0" : "0"}; } ${STIJL}</style></head>
<body>${inhoud}</body></html>`;
}

function kop(d: AfdrukDossier, titel: string): string {
  return `<div class="kop"><strong>${esc(d.organisatie)}</strong><span>${esc(titel)} · ${esc(d.dossierNummer ?? "")}</span></div>`;
}

function adresBlok(d: AfdrukDossier): string {
  return [d.eigenaarNaam, d.eigenaarAdres, [d.eigenaarPostcode, d.eigenaarPlaats].filter(Boolean).join("  ")]
    .filter((r) => r && r.trim() !== "")
    .map((r) => `<div class="regel">${esc(r)}</div>`).join("");
}

function dierRegel(d: AfdrukDossier): string {
  return [d.diersoort, d.ras].filter(Boolean).join(" · ");
}

function bestemming(d: AfdrukDossier): string {
  switch (d.retourwijze) {
    case "URN_MEE": return "de urn gaat mee naar huis";
    case "VERSTROOIEN_VELD": return "de as wordt verstrooid op het veld";
    case "VERSTROOIEN_ZEE": return "de as wordt verstrooid op zee";
    case "BIJZETTEN_GRAF": return `de as wordt bijgezet in het graf${d.grafnummer ? ` (${d.grafnummer})` : ""}`;
    default: return d.retourwijze ? d.retourwijze : "de asbestemming is nog niet bepaald";
  }
}

export function afdrukHtml(soort: AfdrukSoort, d: AfdrukDossier): string {
  const naam = esc(d.dierNaam ?? "—");
  switch (soort) {
    case "CREMATIECERTIFICAAT": {
      // De opbouw komt uit het bronrapport (crematorium-certificaat-report.jrxml): een aanhef, dan
      // "hebben wij respectvol en waardig … op <datum>" met de naam van het dier eruit gelicht, het
      // gedicht, "Oneindig", en de afsluiting namens het crematorium. De resomeer-variant is in het
      // origineel een apart rapport met dezelfde teksten in een andere indeling — één sjabloon
      // volstaat dus, met alleen het werkwoord dat meebeweegt.
      const tijd = tijdstipUit(d.planningDatum);
      return pagina("Crematiecertificaat", "A4", `<div class="blad">${kop(d, "Crematiecertificaat")}
        <div class="cert">
          <p class="cert-aanhef">${esc(aanhefUit(d.eigenaarNaam))}</p>
          <div class="naam">${naam}</div>
          <p class="cert-zin">hebben wij respectvol en waardig ${verwerkingWoord(d.verwerkingsvorm)}<br/>
            op ${datumLang(d.planningDatum)}${tijd}</p>
          <p class="cert-gedicht">${CERTIFICAAT_GEDICHT.map(esc).join("<br/>")}</p>
          <p class="cert-oneindig">Oneindig</p>
          <p class="cert-namens">Namens ${esc(d.organisatie)}<br/>
            Sterkte met het verlies van uw huisdier en trouwe kameraad</p>
          ${d.identificatieNummer ? `<p class="stil">Identificatienummer ${esc(d.identificatieNummer)}</p>` : ""}
          <p class="onder">Dossier ${esc(d.dossierNummer ?? "")} · ${esc(dierRegel(d))}</p>
        </div></div>`);
    }
    case "HERINNERINGSKAART":
      return pagina("Herinneringskaart", "A5", `<div class="blad"><div class="kaart">
          <p class="stil">Ter herinnering aan</p>
          <div class="naam">${naam}</div>
          <p>${esc(dierRegel(d))}</p>
          <p>${datumLang(d.datumOverlijden)}</p>
          <p class="stil">Met warme groet, ${esc(d.organisatie)}</p>
        </div></div>`);
    case "BEGELEIDENDE_BRIEF":
      return pagina("Begeleidende brief", "A4", `<div class="blad">${kop(d, "Begeleidende brief")}
        <div class="etiket" style="border:0;padding:0">${adresBlok(d)}</div>
        <p class="stil" style="margin-top:8mm">${datumLang(new Date().toISOString().slice(0, 10))}</p>
        <p>Geachte ${esc(d.eigenaarNaam ?? "heer, mevrouw")},</p>
        <p>Hierbij ontvangt u de bevestiging van de uitvaart van ${naam}. De ${esc((d.uitvaartvormNaam ?? "uitvaart").toLowerCase())} is gepland op ${datumLang(d.planningDatum)}; ${esc(bestemming(d))}.</p>
        <p>Heeft u vragen, dan kunt u ons bereiken onder vermelding van dossier ${esc(d.dossierNummer ?? "")}.</p>
        <p>Met vriendelijke groet,<br/>${esc(d.organisatie)}</p></div>`);
    case "AS_RETOURBRIEF":
      return pagina("As-retourbrief", "A4", `<div class="blad">${kop(d, "As-retourbrief")}
        <div class="etiket" style="border:0;padding:0">${adresBlok(d)}</div>
        <p class="stil" style="margin-top:8mm">${datumLang(new Date().toISOString().slice(0, 10))}</p>
        <p>Geachte ${esc(d.eigenaarNaam ?? "heer, mevrouw")},</p>
        <p>De as van ${naam} is gereed. ${esc(bestemming(d)).replace(/^./, (c) => c.toUpperCase())}.${d.asUrnNummer ? ` As-/urnnummer: ${esc(d.asUrnNummer)}.` : ""}</p>
        <p>U kunt de urn ophalen op ons adres; neemt u deze brief en een legitimatiebewijs mee.</p>
        <p>Met vriendelijke groet,<br/>${esc(d.organisatie)}</p></div>`);
    case "IDENTIFICATIELABEL":
      return pagina("Identificatielabel", "label", `<div class="label"><strong>${naam} · ${esc(d.dossierNummer ?? "")}</strong>
        ${esc(dierRegel(d))}${d.gewichtKg != null ? ` · ${String(d.gewichtKg).replace(".", ",")} kg` : ""}<br/>
        ${esc(d.uitvaartvormNaam ?? "")} · ${datum(d.planningDatum)}${d.identificatieNummer ? ` · ID ${esc(d.identificatieNummer)}` : ""}</div>`);
    case "URNLABEL":
      return pagina("Urnlabel", "label", `<div class="label"><strong>${naam}</strong>
        ${esc(d.eigenaarNaam ?? "")}<br/>${esc(bestemming(d))}${d.asUrnNummer ? ` · ${esc(d.asUrnNummer)}` : ""}</div>`);
    case "ADRESETIKET":
      return pagina("Adresetiket", "label", `<div class="label"><strong>${esc(d.eigenaarNaam ?? "")}</strong>
        ${esc(d.eigenaarAdres ?? "")}<br/>${esc([d.eigenaarPostcode, d.eigenaarPlaats].filter(Boolean).join("  "))}</div>`);
    case "CREMATIEFACTUUR": {
      // De eigen indeling van de crematiefactuur, voor voorgedrukt papier.
      //
      // Alles staat op een vaste plek in plaats van mee te schuiven met de inhoud: op briefpapier
      // met een gedrukt hoofd moet de tekst naast en onder wat er al staat vallen, en niet ernaast
      // schuiven omdat er een regel bij komt. De maten komen uit crematorium-factuur-report.jrxml,
      // omgerekend van punten naar millimeters.
      //
      // De bovenste 54 mm blijft daarom leeg: dat is het gedrukte briefhoofd. Het afzenderblok dat
      // in het bronrapport links staat is hier weggelaten — dat adres staat al op het papier, en
      // het dossier draagt het niet, dus het verzinnen zou erger zijn dan het weglaten.
      const btw = btwPerTarief(d.regels);
      const rijen = d.regels.map((rg) => `<tr>
        <td>${esc(rg.omschrijving)}</td>
        <td class="b">${euro(rg.prijs)}</td>
        <td class="b">${rg.aantal ?? ""}</td>
        <td class="b">${euro(rg.regelTotaal)}</td></tr>`).join("");
      const mm = (pt: number) => (pt * 0.3528).toFixed(1) + "mm";
      // De voettekst begint waar de laatste band van het bronrapport begint: 842 − 215 punten.
      const voet = (pt: number) => mm(842 - 215 + pt);
      const bedragRegel = (yPt: number, label: string, bedrag: number | null, vet = false) => `
        <p class="p${vet ? " vet" : ""}" style="left:${mm(335)};top:${voet(yPt)};width:${mm(140)}">${esc(label)}</p>
        <p class="p r${vet ? " vet" : ""}" style="left:${mm(430)};top:${voet(yPt)};width:${mm(95)}">${euro(bedrag)}</p>`;

      return pagina("Crematiefactuur", "A4", `<div class="vf">
        <p class="p" style="left:${mm(358)};top:${mm(155)};width:${mm(208)}">${esc(d.eigenaarNaam)}</p>
        <p class="p" style="left:${mm(358)};top:${mm(175)};width:${mm(208)}">${esc(d.eigenaarAdres)}</p>
        <p class="p" style="left:${mm(358)};top:${mm(195)};width:${mm(208)}">${esc([d.eigenaarPostcode, d.eigenaarPlaats].filter(Boolean).join("  "))}</p>

        <p class="p" style="left:${mm(71)};top:${mm(304)};width:${mm(100)}">Factuurdatum:</p>
        <p class="p" style="left:${mm(171)};top:${mm(304)};width:${mm(100)}">${datum(d.factuur?.factuurDatum)}</p>
        <p class="p" style="left:${mm(71)};top:${mm(324)};width:${mm(100)}">Factuurnummer:</p>
        <p class="p vet" style="left:${mm(171)};top:${mm(324)};width:${mm(100)}">${esc(d.factuur?.factuurNummer)}</p>

        <p class="p" style="left:${mm(70)};top:${mm(375)};width:${mm(455)}">Betreft het ${verwerkingWoord(d.verwerkingsvorm) === "geresomeerd" ? "resomeren" : verwerkingWoord(d.verwerkingsvorm) === "begraven" ? "begraven" : "cremeren"} van ${esc(d.dierNaam)}${d.planningDatum ? ` op ${datum(d.planningDatum)}` : ""}</p>

        <p class="p" style="left:${mm(71)};top:${mm(412)};width:${mm(100)}">Diercategorie:</p>
        <p class="p" style="left:${mm(171)};top:${mm(412)};width:${mm(160)}">${esc(d.diersoort)}</p>
        <p class="p" style="left:${mm(70)};top:${mm(432)};width:${mm(100)}">Chipnummer:</p>
        <p class="p" style="left:${mm(170)};top:${mm(432)};width:${mm(355)}">${esc(d.chipnummer ?? "—")}</p>
        <p class="p" style="left:${mm(70)};top:${mm(452)};width:${mm(100)}">Soort uitvaart:</p>
        <p class="p" style="left:${mm(170)};top:${mm(452)};width:${mm(160)}">${esc(d.uitvaartvormNaam)}</p>

        <div class="regels"><table><tbody>${rijen}</tbody></table></div>

        <div class="lijn" style="left:${mm(70)};right:${mm(70)};top:${voet(-6)}"></div>
        ${btw.map((b, i) => `<p class="p" style="left:${mm(70)};top:${voet(80 + i * 20)};width:${mm(260)}">BTW ${b.percentage}%: ${euro(b.bedrag)}</p>`).join("")}
        ${bedragRegel(40, "Totaalprijs", d.totaalBedrag, true)}
        ${(d.betaaldBedrag ?? 0) > 0 ? bedragRegel(70, "Reeds voldaan", d.betaaldBedrag) : ""}
        ${bedragRegel(100, "Nog te voldoen", d.openstaandBedrag, true)}

        <p class="p" style="left:${mm(70)};top:${voet(120)};width:${mm(455)}">Wij verzoeken u vriendelijk de factuur na ontvangst te betalen, met vermelding van het factuurnummer.</p>
        <p class="p" style="left:${mm(70)};top:${voet(180)};width:${mm(455)}">Indien uw betaling dit schrijven heeft gekruist, dan kunt u deze herinnering als niet geschreven beschouwen.</p>
      </div>`);
    }
    case "FACTUUR": {
      const rijen = d.regels.map((r) => `<tr><td>${esc(r.omschrijving)}</td><td class="r">${r.aantal ?? ""}</td><td class="r">${euro(r.prijs)}</td><td class="r">${r.btwPercentage ?? 0}%</td><td class="r">${euro(r.regelTotaal)}</td></tr>`).join("");
      return pagina("Factuur", "A4", `<div class="blad">${kop(d, "Factuur")}
        <div class="etiket" style="border:0;padding:0">${adresBlok(d)}</div>
        <h1 style="margin-top:8mm">Factuur ${esc(d.factuur?.factuurNummer ?? "")}</h1>
        <dl class="feiten"><dt>Factuurdatum</dt><dd>${datum(d.factuur?.factuurDatum)}</dd><dt>Vervaldatum</dt><dd>${datum(d.factuur?.vervaldatum)}</dd><dt>Dossier</dt><dd>${esc(d.dossierNummer ?? "")} · ${naam}</dd></dl>
        <table><thead><tr><th>Omschrijving</th><th class="r">Aantal</th><th class="r">Prijs</th><th class="r">Btw</th><th class="r">Totaal</th></tr></thead>
        <tbody>${rijen}</tbody>
        <tfoot><tr class="totaal"><td colspan="4" class="r">Subtotaal</td><td class="r">${euro(d.subtotaal)}</td></tr>
        <tr class="totaal"><td colspan="4" class="r">Btw</td><td class="r">${euro(d.btwTotaal)}</td></tr>
        <tr class="totaal"><td colspan="4" class="r">Totaal</td><td class="r">${euro(d.totaalBedrag)}</td></tr>
        ${(d.betaaldBedrag ?? 0) > 0 ? `<tr class="totaal"><td colspan="4" class="r">Reeds betaald</td><td class="r">${euro(d.betaaldBedrag)}</td></tr><tr class="totaal"><td colspan="4" class="r">Te betalen</td><td class="r">${euro(d.openstaandBedrag)}</td></tr>` : ""}</tfoot></table>
        <p class="onder">Wij verzoeken u het bedrag vóór de vervaldatum te voldoen onder vermelding van het factuurnummer.</p></div>`);
    }
  }
}

/** Zet de afdruk in een printvenster. */
export function drukAf(html: string): boolean {
  const venster = window.open("", "_blank");
  if (!venster) return false;
  venster.document.write(html);
  venster.document.close();
  venster.focus();
  venster.print();
  return true;
}
